import React, { useState, useRef, useEffect } from 'react';
import { Link2, FileText, ArrowRight, Loader2, CheckCircle, AlertCircle, ChevronDown, Quote, Play, ExternalLink, AlertTriangle, Info, RefreshCw, ThumbsUp, ThumbsDown, Target, Mic, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn, getExpertInfo, formatProcessingTime, formatDuration } from '../lib/utils';
import { SEVERITY, CATEGORIES, EXPERT_REGISTRY, FEATURED_EXPERTS } from '../lib/constants';
import api from '../lib/api';
import { ExpertAvatar, ExpertAvatarRow } from '../components/ui/Avatar';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';

export function LandingPage() {
  // Input states
  const [activeTab, setActiveTab] = useState('url');
  const [textInput, setTextInput] = useState('');
  const [urlInput, setURLInput] = useState('');
  const [urlContext, setURLContext] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [urlMetadata, setUrlMetadata] = useState(null);
  const [urlHero, setUrlHero] = useState(null);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [urlFetchStatus, setUrlFetchStatus] = useState(null);

  // Voice
  const {
    isRecording,
    duration,
    transcript,
    startRecording,
    stopRecording,
    clearTranscript,
  } = useVoiceRecorder({
    onTranscript: (text) => setTextInput(text),
    onError: (msg) => toast.error(msg),
    maxDuration: 120,
  });

  // Analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  // Refs
  const resultsRef = useRef(null);
  const inputRef = useRef(null);

  // ─── URL Fetching ───
  const handleFetchUrl = async () => {
    if (!urlInput.trim()) return;
    setIsFetchingUrl(true);
    setUrlFetchStatus(null);
    try {
      const res = await api.scrapeUrl(urlInput);
      setUrlFetchStatus('success');
      setUrlContent(res.content);
      setUrlMetadata(res.metadata || null);
      setUrlHero(res.hero || null);
    } catch (err) {
      setUrlFetchStatus('error');
      toast.error(err.message || 'Failed to fetch URL');
    } finally {
      setIsFetchingUrl(false);
    }
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    let input = '';
    let inputType = 'text';

    if (transcript) {
      input = transcript;
      inputType = 'voice';
    } else if (activeTab === 'url') {
      input = urlInput + (urlContext ? `\n\nContext: ${urlContext}` : '');
      inputType = 'url';
    } else {
      input = textInput;
      inputType = 'text';
    }

    if (!input || input.trim().length < 10) {
      toast.error('Tell us a bit more — at least a sentence about what happened.');
      return;
    }

    setIsAnalyzing(true);
    setResult(null);
    setError(null);
    setFeedbackGiven(false);

    try {
      const response = await api.analyze(
        input.trim(),
        inputType,
        activeTab === 'url' ? urlContent : null,
        null,
        activeTab === 'url' ? urlMetadata : null,
        activeTab === 'url' ? urlHero : null,
      );
      setResult(response);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (err) {
      setError(err.message || 'Analysis failed. Please try again.');
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setTextInput('');
    setURLInput('');
    setURLContext('');
    setUrlContent('');
    setUrlMetadata(null);
    setUrlHero(null);
    setUrlFetchStatus(null);
    setFeedbackGiven(false);
    clearTranscript();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFeedback = async (isHelpful) => {
    try {
      await api.submitFeedback(null, isHelpful, urlInput || textInput, result?.patterns?.length || 0);
      setFeedbackGiven(true);
      toast.success('Thanks — your feedback shapes better pattern recognition.');
    } catch { /* silent */ }
  };

  const hasInput = () => {
    if (transcript) return true;
    if (activeTab === 'text') return textInput.trim().length > 0;
    if (activeTab === 'url') return urlInput.trim().length > 0;
    return false;
  };

  return (
    <main className="min-h-screen bg-[#08080A] relative">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-25%] left-[5%] w-[700px] h-[700px] bg-[#14B8A6]/[0.025] rounded-full blur-[180px]" />
        <div className="absolute bottom-[-15%] right-[5%] w-[500px] h-[500px] bg-[#F59E0B]/[0.015] rounded-full blur-[180px]" />
      </div>

      {/* ═══ NAV ═══ */}
      <nav className="relative z-10 border-b border-[#1F1F23]/40">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="text-[16px] font-bold text-[#FAFAFA] tracking-tight">Patterns</span>
          <a href="https://www.lennyspodcast.com/" target="_blank" rel="noopener noreferrer"
            className="text-[13px] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex items-center gap-1.5">
            Built on Lenny's Podcast data
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </nav>

      <div className="relative z-10">

        {/* ═══════════════════════════════════════════════
            HERO — The Truth
        ═══════════════════════════════════════════════ */}
        <section className="max-w-3xl mx-auto px-6 pt-20 sm:pt-28 pb-6 text-center">
          <h1 className="text-[38px] sm:text-[56px] font-extrabold text-[#FAFAFA] leading-[1.05] tracking-[-0.035em] mb-6 animate-fade-in">
            You launched.
            <br />
            <span className="gradient-text">Nobody bought.</span>
          </h1>

          <p className="text-[17px] sm:text-[19px] text-[#A1A1AA] leading-[1.7] max-w-[540px] mx-auto animate-fade-in" style={{ animationDelay: '150ms' }}>
            That gap between attention and conversion? There's a pattern hiding in it. We find it.
          </p>
        </section>

        {/* ═══════════════════════════════════════════════
            THE DROP — What Is This
        ═══════════════════════════════════════════════ */}
        <section className="max-w-2xl mx-auto px-6 pt-8 pb-12">
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div className="relative p-6 sm:p-8 rounded-2xl bg-[#0F0F12]/80 border border-[#1F1F23]/60">
              <p className="text-[15px] sm:text-[16px] text-[#A1A1AA] leading-[1.85]">
                <span className="text-[#E4E4E7] font-semibold">Patterns</span> reads your product the way world-class founders read products.
                It looks at your headline, your pricing, your CTA, your social proof — and finds the
                <span className="text-[#E4E4E7]"> specific, hidden patterns </span>
                that explain the gap between your launch and your results.
              </p>
              <p className="text-[15px] sm:text-[16px] text-[#A1A1AA] leading-[1.85] mt-4">
                Grounded in insights from
                <span className="text-[#14B8A6]"> 300+ product leaders</span> — April Dunford on positioning, Shreyas Doshi on product decisions, Elena Verna on growth.
                Not generic advice. Your specific situation, their specific lens.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════
            VOICE — Primary Input
        ═══════════════════════════════════════════════ */}
        <section className="max-w-2xl mx-auto px-6 pb-6 animate-fade-in" style={{ animationDelay: '400ms' }}>
          <div className="flex flex-col items-center">
            {/* Voice Button */}
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isAnalyzing}
              className={cn(
                'relative w-28 h-28 sm:w-32 sm:h-32 rounded-full',
                'transition-all duration-300',
                'focus-visible:outline-none focus-visible:ring-4',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                isRecording ? [
                  'bg-gradient-to-br from-[#14B8A6] to-[#0D9488]',
                  'scale-110',
                  'shadow-[0_0_60px_rgba(20,184,166,0.35)]',
                ] : [
                  'bg-[#0F0F12]',
                  'border-2 border-[#27272A]',
                  'hover:border-[#14B8A6]/40',
                  'hover:shadow-[0_0_40px_rgba(20,184,166,0.12)]',
                  'animate-breathe',
                ]
              )}
            >
              <div className="flex items-center justify-center h-full">
                {isRecording ? (
                  <Square className="w-9 h-9 text-[#08080A]" />
                ) : (
                  <Mic className="w-9 h-9 text-[#71717A]" />
                )}
              </div>
              {isRecording && (
                <>
                  <span className="absolute inset-0 rounded-full border-2 border-[#14B8A6] animate-ping opacity-25" />
                  <span className="absolute inset-0 rounded-full border-2 border-[#14B8A6] animate-pulse-ring opacity-15" style={{ animationDelay: '0.5s' }} />
                </>
              )}
            </button>

            {/* Voice Status */}
            <div className="mt-5 text-center">
              {isRecording ? (
                <>
                  <p className="text-[16px] text-[#14B8A6] font-semibold">
                    Listening · {formatDuration(duration)}
                  </p>
                  <p className="text-[14px] text-[#71717A] mt-1">Tap to stop</p>
                </>
              ) : (
                <>
                  <p className="text-[16px] text-[#E4E4E7] font-medium">Tell us what happened</p>
                  <p className="text-[14px] text-[#71717A] mt-1">Tap and describe your launch in your own words</p>
                </>
              )}
            </div>
          </div>

          {/* Voice Transcript */}
          {transcript && (
            <div className="mt-6 p-5 bg-[#0F0F12] border border-[#1F1F23] rounded-xl animate-fade-in">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-[#71717A]">Your voice input</span>
                <button
                  onClick={() => { clearTranscript(); setTextInput(''); }}
                  className="text-[13px] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
              <p className="text-[15px] text-[#E4E4E7] leading-[1.7] italic">"{transcript}"</p>
            </div>
          )}
        </section>

        {/* ─── Divider ─── */}
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center gap-4 py-4">
            <div className="flex-1 h-px bg-[#1F1F23]" />
            <span className="text-[13px] text-[#52525B] font-medium">or</span>
            <div className="flex-1 h-px bg-[#1F1F23]" />
          </div>
        </div>

        {/* ═══════════════════════════════════════════════
            SECONDARY INPUT — URL / Text
        ═══════════════════════════════════════════════ */}
        <section ref={inputRef} className="max-w-2xl mx-auto px-6 pb-6 animate-fade-in" style={{ animationDelay: '500ms' }}>
          <div className="bg-[#0F0F12] border border-[#1F1F23] rounded-2xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-[#1F1F23]">
              {[
                { id: 'url', label: 'Paste a URL', icon: Link2 },
                { id: 'text', label: 'Type it out', icon: FileText },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 h-12 text-[14px] font-medium transition-all duration-200',
                    activeTab === tab.id
                      ? 'text-[#FAFAFA] border-b-2 border-[#14B8A6] bg-[#14B8A6]/[0.04]'
                      : 'text-[#71717A] hover:text-[#A1A1AA]'
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* URL Tab */}
            {activeTab === 'url' && (
              <div className="p-5 space-y-4">
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-11 flex items-center justify-center border-r border-[#27272A] text-[#71717A]">
                    <Link2 className="w-4 h-4" />
                  </div>
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => { setURLInput(e.target.value); setUrlFetchStatus(null); }}
                    placeholder="yourproduct.com"
                    disabled={isAnalyzing}
                    onBlur={handleFetchUrl}
                    onKeyDown={(e) => e.key === 'Enter' && handleFetchUrl()}
                    className={cn(
                      'w-full h-[48px] pr-12',
                      'bg-[#161619] border border-[#27272A] rounded-xl',
                      'text-[15px] text-[#FAFAFA] placeholder:text-[#52525B]',
                      'focus:outline-none focus:ring-1 focus:ring-[#14B8A6]/30 focus:border-[#14B8A6]/30',
                      'transition-all duration-200 disabled:opacity-50'
                    )}
                    style={{ paddingLeft: '52px' }}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isFetchingUrl && <Loader2 className="w-4 h-4 text-[#14B8A6] animate-spin" />}
                    {urlFetchStatus === 'success' && <CheckCircle className="w-4 h-4 text-[#34D399]" />}
                    {urlFetchStatus === 'error' && <AlertCircle className="w-4 h-4 text-[#F87171]" />}
                  </div>
                </div>

                {urlFetchStatus === 'success' && (
                  <p className="text-[14px] text-[#34D399] flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Page loaded — {urlContent.length > 0 ? `${Math.round(urlContent.length / 1000)}k characters extracted` : 'ready'}
                  </p>
                )}

                <div>
                  <label className="block text-[13px] text-[#71717A] mb-2 font-medium">
                    What happened after launch? <span className="text-[#52525B]">(helps a lot)</span>
                  </label>
                  <textarea
                    value={urlContext}
                    onChange={(e) => setURLContext(e.target.value)}
                    placeholder="e.g., Got 500 signups from Product Hunt but only 8 converted to paid..."
                    disabled={isAnalyzing}
                    rows={3}
                    className={cn(
                      'w-full p-4 bg-[#161619] border border-[#27272A] rounded-xl',
                      'text-[15px] text-[#FAFAFA] leading-[1.7] placeholder:text-[#52525B]',
                      'resize-none focus:outline-none focus:ring-1 focus:ring-[#14B8A6]/30',
                      'transition-all duration-200 disabled:opacity-50'
                    )}
                  />
                </div>
              </div>
            )}

            {/* Text Tab */}
            {activeTab === 'text' && (
              <div className="p-5">
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={"Describe what you built, how you launched, and what happened.\n\nExample: \"I built a Notion template for startup founders. Priced at $19/month. Got 200 signups from Product Hunt but only 3 converted to paying customers...\""}
                  disabled={isAnalyzing}
                  rows={5}
                  className={cn(
                    'w-full p-4 bg-[#161619] border border-[#27272A] rounded-xl',
                    'text-[15px] text-[#FAFAFA] leading-[1.7] placeholder:text-[#52525B]',
                    'resize-none focus:outline-none focus:ring-1 focus:ring-[#14B8A6]/30',
                    'transition-all duration-200 disabled:opacity-50'
                  )}
                />
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-center mt-6">
            <button
              onClick={handleSubmit}
              disabled={!hasInput() || isAnalyzing}
              className={cn(
                'inline-flex items-center justify-center gap-2.5 h-13 px-10',
                'rounded-xl font-semibold text-[15px]',
                'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-[#08080A]',
                'hover:from-[#2DD4BF] hover:to-[#14B8A6]',
                'hover:shadow-[0_8px_24px_rgba(20,184,166,0.35)]',
                'hover:-translate-y-0.5 active:translate-y-0',
                'transition-all duration-200',
                'disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
                'shadow-[0_4px_16px_rgba(20,184,166,0.2)]',
                'w-full sm:w-auto sm:min-w-[260px]'
              )}
              style={{ height: '52px' }}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Finding patterns...</span>
                </>
              ) : (
                <>
                  <span>Find the Patterns</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </section>

        {/* ═══ LOADING ═══ */}
        {isAnalyzing && (
          <section className="max-w-2xl mx-auto px-6 py-16 animate-fade-in">
            <div className="text-center">
              <div className="relative w-16 h-16 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full border-[2px] border-[#1F1F23]" />
                <div className="absolute inset-0 rounded-full border-[2px] border-transparent border-t-[#14B8A6] animate-spin" style={{ animationDuration: '1.2s' }} />
                <div className="absolute inset-3 rounded-full bg-[#14B8A6]/5 animate-glow-pulse" />
              </div>
              <h2 className="text-[20px] font-semibold text-[#FAFAFA] mb-3">Thinking deeply...</h2>
              <p className="text-[15px] text-[#A1A1AA] mb-8">
                Analyzing your product through 8 expert lenses
              </p>
              <div className="w-full max-w-[280px] mx-auto">
                <div className="h-1.5 bg-[#1F1F23] rounded-full overflow-hidden">
                  <div className="h-full rounded-full animate-progress" style={{ background: 'linear-gradient(90deg, #14B8A6, #2DD4BF)' }} />
                </div>
                <p className="text-[13px] text-[#71717A] mt-3">Usually takes 15–20 seconds</p>
              </div>
              <LoadingMessages />
            </div>
          </section>
        )}

        {/* ═══ ERROR ═══ */}
        {error && !isAnalyzing && (
          <section className="max-w-2xl mx-auto px-6 py-10 animate-fade-in">
            <div className="bg-[#F87171]/[0.06] border border-[#F87171]/20 rounded-2xl p-8 text-center">
              <AlertCircle className="w-8 h-8 text-[#F87171] mx-auto mb-4" />
              <p className="text-[16px] text-[#F87171] mb-5 leading-[1.6]">{error}</p>
              <button onClick={handleReset}
                className="text-[14px] text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors font-medium underline underline-offset-4">
                Try again
              </button>
            </div>
          </section>
        )}

        {/* ═══════════════════════════════════════════════
            RESULTS
        ═══════════════════════════════════════════════ */}
        {result && !isAnalyzing && (
          <section ref={resultsRef} className="max-w-2xl mx-auto px-6 pt-8 pb-20">
            {/* Divider */}
            <div className="flex items-center gap-4 mb-10 animate-fade-in">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#14B8A6]/30 to-transparent" />
              <span className="text-[13px] text-[#14B8A6] font-semibold uppercase tracking-[0.12em]">Your Patterns</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#14B8A6]/30 to-transparent" />
            </div>

            {/* Sources */}
            <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl border mb-8 animate-fade-in"
              style={{ background: 'linear-gradient(90deg, rgba(20,184,166,0.06) 0%, transparent 100%)', borderColor: 'rgba(20,184,166,0.12)' }}>
              <CheckCircle className="w-4 h-4 text-[#14B8A6] flex-shrink-0" />
              <p className="text-[14px] text-[#A1A1AA]">
                Cross-referenced with <span className="text-[#E4E4E7] font-medium">{result.sourcesUsed || 0} expert insights</span>
                <span className="text-[#71717A]"> · {formatProcessingTime(result.processingTime)}</span>
              </p>
            </div>

            {/* Meta Insight */}
            {result.metaInsight && (
              <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-[#14B8A6]/[0.06] to-transparent border border-[#14B8A6]/10 animate-fade-in" style={{ animationDelay: '100ms' }}>
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-semibold text-[#14B8A6] uppercase tracking-[0.08em] mb-2">The Core Issue</p>
                    <p className="text-[16px] text-[#E4E4E7] leading-[1.75]">{result.metaInsight}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Context Card */}
            <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
              <ContextCard input={urlInput || textInput || transcript} productContext={result.productContext} signals={result.signals} />
            </div>

            {/* Pattern Cards */}
            <div className="space-y-8 mt-8">
              {result.patterns?.map((pattern, index) => (
                <div key={pattern.id || index} className="animate-fade-in-slow" style={{ animationDelay: `${300 + index * 200}ms` }}>
                  <PatternCard pattern={pattern} index={index} />
                </div>
              ))}
            </div>

            {/* Feedback */}
            <div className="mt-12 animate-fade-in" style={{ animationDelay: '700ms' }}>
              {!feedbackGiven ? (
                <div className="p-6 bg-[#0F0F12] border border-[#1F1F23] rounded-2xl text-center">
                  <p className="text-[15px] text-[#A1A1AA] mb-4">Were these patterns insightful?</p>
                  <div className="flex justify-center gap-3">
                    <button onClick={() => handleFeedback(true)}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1F1F23] text-[#A1A1AA] hover:bg-[#14B8A6]/10 hover:text-[#14B8A6] transition-all text-[14px] font-medium">
                      <ThumbsUp className="w-4 h-4" /> Yes, helpful
                    </button>
                    <button onClick={() => handleFeedback(false)}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1F1F23] text-[#A1A1AA] hover:bg-[#F87171]/10 hover:text-[#F87171] transition-all text-[14px] font-medium">
                      <ThumbsDown className="w-4 h-4" /> Not really
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-5 bg-[#14B8A6]/[0.06] border border-[#14B8A6]/12 rounded-2xl text-center">
                  <p className="text-[15px] text-[#14B8A6]">Thanks — this helps improve pattern recognition for everyone.</p>
                </div>
              )}
            </div>

            {/* Analyze Another */}
            <div className="flex justify-center mt-10">
              <button onClick={handleReset}
                className="inline-flex items-center gap-2.5 px-7 py-3 rounded-xl bg-[#1F1F23] border border-[#27272A] text-[#E4E4E7] hover:bg-[#27272A] transition-all text-[15px] font-semibold">
                <RefreshCw className="w-4 h-4" />
                Analyze Another Launch
              </button>
            </div>
          </section>
        )}

        {/* ═══ EXPERT PANEL ═══ */}
        {!result && !isAnalyzing && (
          <section className="max-w-2xl mx-auto px-6 py-14">
            <div className="border-t border-[#1F1F23] pt-10 text-center">
              <p className="text-[13px] text-[#71717A] mb-5 font-medium">Insights informed by</p>
              <div className="flex justify-center mb-4">
                <ExpertAvatarRow experts={FEATURED_EXPERTS} maxShow={5} size={44} />
                <div className="w-11 h-11 rounded-full -ml-2 flex items-center justify-center bg-[#1F1F23] border-2 border-[#08080A] text-[12px] font-semibold text-[#71717A]">
                  +47
                </div>
              </div>
              <a href="https://www.lennyspodcast.com/" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[14px] text-[#71717A] hover:text-[#14B8A6] transition-colors">
                from Lenny's Podcast
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </section>
        )}

        {/* ═══ FOOTER ═══ */}
        <footer className="max-w-2xl mx-auto px-6 py-8 text-center border-t border-[#1F1F23]/40">
          <p className="text-[13px] text-[#71717A] leading-[1.6]">
            Pattern recognition from 33,000+ expert insights · 847 hours of conversations · 300+ product leaders
          </p>
        </footer>
      </div>
    </main>
  );
}


/* ═══════════════════════════════════════════════
   SUB-COMPONENTS — Inline for cohesion
═══════════════════════════════════════════════ */

function LoadingMessages() {
  const [idx, setIdx] = React.useState(0);
  const msgs = [
    'Extracting product signals from your input...',
    'Checking value proposition clarity...',
    'Analyzing conversion architecture...',
    'Evaluating pricing psychology...',
    'Scanning trust and credibility signals...',
    'Matching against expert frameworks...',
    'Identifying the deepest patterns...',
    'Synthesizing your recommendations...',
  ];
  React.useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % msgs.length), 2200);
    return () => clearInterval(t);
  }, [msgs.length]);
  return (
    <p className="text-[14px] text-[#71717A] mt-8 transition-opacity duration-500" key={idx}>{msgs[idx]}</p>
  );
}

function ContextCard({ input, productContext, signals }) {
  const [expanded, setExpanded] = useState(false);
  const pills = [
    { label: 'Product', value: productContext?.type },
    { label: 'Pricing', value: productContext?.pricing },
    { label: 'Channel', value: productContext?.channel },
    { label: 'Outcome', value: productContext?.outcome },
    { label: 'Stage', value: productContext?.stage },
  ].filter(p => p.value);

  const signalItems = [
    { label: 'Headline', value: signals?.headline_analysis },
    { label: 'CTA', value: signals?.cta_analysis },
    { label: 'Trust', value: signals?.trust_gap },
    { label: 'Audience', value: signals?.audience_clarity },
  ].filter(s => s.value);

  return (
    <div className="bg-[#0F0F12] border border-[#1F1F23] rounded-2xl overflow-hidden">
      <div className="px-6 pt-6 pb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-semibold text-[#71717A] uppercase tracking-[0.08em]">What you shared</span>
          <button onClick={() => setExpanded(!expanded)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[#71717A] hover:text-[#A1A1AA] hover:bg-[#1F1F23] transition-all">
            <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', expanded && 'rotate-180')} />
          </button>
        </div>
        <div className={cn('text-[15px] text-[#A1A1AA] leading-[1.7]', !expanded && 'line-clamp-2')}>
          "{input}"
        </div>
      </div>

      {pills.length > 0 && (
        <div className="px-6 pb-5 flex flex-wrap gap-2">
          {pills.map((pill, i) => (
            <div key={i} className="flex flex-col px-3 py-2 bg-[#161619] border border-[#1F1F23] rounded-lg">
              <span className="text-[11px] font-semibold text-[#71717A] uppercase tracking-[0.06em]">{pill.label}</span>
              <span className="text-[14px] font-medium text-[#E4E4E7] mt-0.5 leading-[1.4]">{pill.value}</span>
            </div>
          ))}
        </div>
      )}

      {signalItems.length > 0 && (
        <div className="border-t border-[#1F1F23] px-6 py-5">
          <p className="text-[13px] font-semibold text-[#71717A] uppercase tracking-[0.08em] mb-3">Signals We Detected</p>
          <div className="space-y-3">
            {signalItems.map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-[13px] font-semibold text-[#52525B] w-[70px] flex-shrink-0 pt-0.5">{s.label}</span>
                <p className="text-[14px] text-[#A1A1AA] leading-[1.6]">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PatternCard({ pattern, index }) {
  const sevConfig = SEVERITY[pattern.severity] || SEVERITY.low;
  const catConfig = CATEGORIES[pattern.category] || CATEGORIES.product;
  const expertInfo = getExpertInfo(pattern.expertQuote?.expert, EXPERT_REGISTRY);
  const TF_COLORS = { 'This week': '#14B8A6', 'Next week': '#A1A1AA', 'This month': '#71717A', 'Next 2 weeks': '#A1A1AA' };
  const SevIcon = pattern.severity === 'high' ? AlertTriangle : pattern.severity === 'medium' ? AlertCircle : Info;

  return (
    <article className="bg-[#0F0F12] border border-[#1F1F23] rounded-2xl overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.2)]">
      {/* Accent bar */}
      <div className="h-1 w-full" style={{ background: sevConfig.gradient }} />

      {/* Header */}
      <div className="px-6 pt-6">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide border"
            style={{ backgroundColor: sevConfig.bgColor, borderColor: sevConfig.borderColor, color: sevConfig.color }}>
            <SevIcon className="w-3 h-3" />
            {sevConfig.label}
          </span>
          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: catConfig.bgColor, color: catConfig.color }}>
            {catConfig.label}
          </span>
        </div>
        <h3 className="text-[22px] sm:text-[26px] font-bold text-[#FAFAFA] leading-[1.2] tracking-[-0.015em]">
          {pattern.name}
        </h3>
      </div>

      <div className="px-6 pt-4 pb-6">
        {/* Explanation */}
        <p className="text-[15px] text-[#A1A1AA] leading-[1.8]">{pattern.explanation}</p>

        {/* Reasoning */}
        {pattern.reasoning && (
          <div className="mt-5 p-5 bg-[#0A0A0C] border border-[#1A1A1E] rounded-xl">
            <p className="text-[13px] font-semibold text-[#71717A] uppercase tracking-[0.08em] mb-2">Chain of Reasoning</p>
            <p className="text-[14px] text-[#A1A1AA] leading-[1.75] italic">{pattern.reasoning}</p>
          </div>
        )}

        {/* Expert quote */}
        {pattern.expertQuote && (
          <div className="relative rounded-xl p-5 mt-6 bg-gradient-to-br from-[#161619] to-[#1C1C20]"
            style={{ borderLeft: '3px solid transparent', borderImage: 'linear-gradient(180deg, #14B8A6, #F59E0B) 1' }}>
            <Quote className="w-7 h-7 text-[#14B8A6]/20 mb-3" />
            <p className="text-[15px] text-[#E4E4E7] leading-[1.75] italic mb-5">"{pattern.expertQuote.text}"</p>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <ExpertAvatar name={pattern.expertQuote.expert} size={40} />
                <div>
                  <p className="text-[15px] font-semibold text-[#FAFAFA]">{pattern.expertQuote.expert}</p>
                  <p className="text-[13px] text-[#71717A]">{expertInfo.role}</p>
                </div>
              </div>
              {pattern.expertQuote.episodeTitle && (
                <a href="https://www.lennyspodcast.com/" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-[#1F1F23] rounded-lg text-[13px] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#E4E4E7] transition-all group">
                  <Play className="w-3.5 h-3.5 text-[#14B8A6]" />
                  <span className="max-w-[180px] truncate">{pattern.expertQuote.episodeTitle}</span>
                  <ExternalLink className="w-3 h-3 text-[#52525B] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {pattern.actions?.length > 0 && (
          <div className="border-t border-[#1F1F23] pt-6 mt-6">
            <h4 className="text-[13px] font-bold text-[#71717A] uppercase tracking-[0.08em] mb-4">What to do next</h4>
            <div className="space-y-3">
              {pattern.actions.map((action, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-[rgba(255,255,255,0.015)] transition-colors">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[14px] font-bold text-[#14B8A6] bg-[rgba(20,184,166,0.08)] border border-[rgba(20,184,166,0.15)]">
                    {action.priority}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <p className="text-[15px] text-[#E4E4E7] leading-[1.6]">{action.action}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[13px] font-medium" style={{ color: TF_COLORS[action.timeframe] || '#71717A' }}>
                        {action.timeframe}
                      </span>
                      {action.effort && (
                        <span className="text-[12px] text-[#71717A] px-2 py-0.5 bg-[#161619] border border-[#1F1F23] rounded-md">
                          {action.effort} effort
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default LandingPage;
